/**
 * La silhouette d'un morceau : décodage du champ `silhouette` du catalogue, et
 * géométrie de la grille qui en découle.
 *
 * # Ce qui a été jeté, et pourquoi
 *
 * `piece-silhouette-shape.ts` dessinait une figure *synthétique* à partir de
 * trois agrégats (`noteCount`, `durationSec`, `difficulty`). Elle avait le
 * mérite de ne rien inventer, mais deux défauts qui l'ont condamnée après
 * comparaison de prototypes rendus :
 *
 *  - elle ne signifiait rien de lisible — deux morceaux très différents en
 *    sortaient jumeaux dès qu'ils partageaient une durée et une densité ;
 *  - surtout, elle **empruntait la grammaire visuelle du piano-roll** (des
 *    barres posées sur un axe temporel) pour encoder des moyennes. Un lecteur
 *    y cherchait donc un passage, une reprise, une main — et le dessin ne
 *    pouvait rien soutenir de tout ça. Une forme qui appelle une lecture
 *    littérale qu'elle ne peut pas honorer est pire qu'une absence de forme.
 *
 * On dessine désormais la **vraie** silhouette, calculée serveur depuis les
 * notes du score. Les agrégats ne sont plus lus du tout.
 *
 * # La grammaire est celle de la minimap du Studio
 *
 * L'algorithme de référence est `lib/engine/components/minimap-renderer.ts` :
 * une grille temps × hauteur, une cellule dessinée par case occupée, l'opacité
 * qui suit la densité de notes. La minimap est **verticale** parce que c'est un
 * rail de défilement ; la carte de morceau est en **paysage**, donc la grille
 * est basculée — les colonnes portent le temps, les lignes portent la hauteur.
 *
 * # Le format, version 1
 *
 * ```
 * "1|<cols>|<rows>|<base64>"
 * ```
 *
 * `cols` et `rows` sont **lus dans la chaîne**, jamais codés en dur : c'est ce
 * qui permettra de changer la granularité côté serveur sans redéployer le
 * front. Le base64 porte `2 × rows × cols` quartets (4 bits), empaquetés deux
 * par octet, poids fort en premier, dans l'ordre main-majeur puis ligne-majeur :
 *
 * ```
 * index = main × rows × cols + ligne × cols + colonne
 * ```
 *
 * `ligne 0` est en haut (la note la plus aiguë), `colonne 0` à gauche (le début
 * du morceau). Un quartet vaut `0` pour une case vide, `1..15` pour une
 * densité **normalisée par main** — chaque main ayant sa propre échelle, une
 * main discrète reste visible en face d'une main bavarde.
 *
 * # Tolérance
 *
 * Toute chaîne qu'on ne sait pas lire — version inconnue, base64 invalide,
 * longueur incohérente avec `cols × rows` — donne `null`, jamais une exception.
 * Un catalogue peut parfaitement servir une version future à un front qui n'a
 * pas encore été redéployé ; ce cas est normal, pas exceptionnel. L'appelant
 * rend alors le repli neutre.
 *
 * Module pur : ni React, ni DOM, ni SVG. Testé dans `__tests__/silhouette.test.ts`.
 */

/** La main à laquelle une case appartient. Porte la couleur au rendu. */
export type SilhouetteHand = "left" | "right";

export type SilhouetteCell = {
  readonly hand: SilhouetteHand;
  /** `0` en haut, sur la note la plus aiguë. */
  readonly row: number;
  /** `0` à gauche, au début du morceau. */
  readonly col: number;
  /**
   * Densité de la case dans `]0, 1]`, normalisée par main. Les cases vides ne
   * sont pas représentées : une cellule qui existe a forcément une densité.
   */
  readonly density: number;
};

export type Silhouette = {
  readonly cols: number;
  readonly rows: number;
  readonly cells: readonly SilhouetteCell[];
};

/** La seule version du format qu'on sache lire. */
const SUPPORTED_VERSION = "1";
/** Valeurs possibles d'un quartet non nul, qui sert de dénominateur à la densité. */
const MAX_QUARTET = 15;
/** Les deux mains, dans l'ordre où le serveur les empaquette. */
const HANDS: readonly SilhouetteHand[] = ["left", "right"];

/**
 * Décode le champ `silhouette` du catalogue.
 *
 * Accepte `undefined` en plus de `null` : le champ est en cours de
 * déploiement côté API, et une pièce servie par une version antérieure ne le
 * porte pas du tout. Un champ absent doit dégrader comme un champ vide, pas
 * casser la page.
 */
export function decodeSilhouette(
  encoded: string | null | undefined,
): Silhouette | null {
  if (typeof encoded !== "string") {
    return null;
  }

  const parts = encoded.split("|");

  if (parts.length !== 4 || parts[0] !== SUPPORTED_VERSION) {
    return null;
  }

  const cols = parsePositiveInteger(parts[1]);
  const rows = parsePositiveInteger(parts[2]);

  if (cols === null || rows === null) {
    return null;
  }

  const bytes = decodeBase64(parts[3]);

  // Deux quartets par octet, donc exactement `rows × cols` octets pour les
  // deux mains. Une longueur qui ne tombe pas juste signifie qu'on ne lit pas
  // la grille qu'on croit : mieux vaut ne rien dessiner que dessiner décalé.
  if (bytes === null || bytes.length !== rows * cols) {
    return null;
  }

  const cellsPerHand = rows * cols;
  const cells: SilhouetteCell[] = [];

  for (let index = 0; index < cellsPerHand * HANDS.length; index++) {
    const byte = bytes[index >> 1];
    const quartet = index % 2 === 0 ? byte >> 4 : byte & 0x0f;

    if (quartet === 0) {
      continue;
    }

    const withinHand = index % cellsPerHand;

    cells.push({
      hand: HANDS[Math.floor(index / cellsPerHand)],
      row: Math.floor(withinHand / cols),
      col: withinHand % cols,
      density: quartet / MAX_QUARTET,
    });
  }

  return { cols, rows, cells };
}

/** Le cadre de dessin, en unités du repère SVG. */
export type SilhouetteBox = {
  readonly width: number;
  readonly height: number;
  /**
   * Espace laissé entre deux cases voisines. Réglage esthétique : il est tenu
   * par le composant de rendu, qui est le seul endroit où on le fait varier.
   */
  readonly gap: number;
};

/** Une case, placée dans le cadre. */
export type SilhouetteRect = SilhouetteCell & {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
};

/**
 * Taille plancher d'une marque. Une marque qui tombe à zéro (cadre minuscule,
 * ou `gap` plus large que la maille) disparaîtrait du dessin sans que la figure
 * change de sens — on préfère un point ténu à un trou.
 */
const MIN_CELL_SIZE = 0.5;

/**
 * Une tranche de rangs à montrer : un **rognage**, pas une mise à l'échelle.
 *
 * C'est ce dont la pochette d'une playlist a besoin. Elle empile une bande par
 * morceau, donc chaque bande est deux à quatre fois moins haute qu'une tuile —
 * et si on y comprimait la grille entière, la maille cesserait d'être carrée et
 * la figure se lirait comme des barres, exactement ce que `silhouetteRects`
 * s'emploie à éviter juste en dessous. En rognant, la maille garde le pas
 * qu'elle a sur la tuile (`largeur / cols`) et la figure reste elle-même : on
 * en voit moins, on ne la déforme pas.
 *
 * `rowStart` est un numéro de rang, `rowCount` le nombre de rangs que le cadre
 * représente désormais. Les cases hors de la fenêtre ne sont pas rendues.
 */
export type SilhouetteRowWindow = {
  readonly rowStart: number;
  readonly rowCount: number;
};

/**
 * Place les cases dans le cadre.
 *
 * # La marque est un carré, la maille ne l'est pas forcément
 *
 * La grille occupe le cadre entier — pas de marge intérieure : sur la carte la
 * figure est en fond perdu, et sur la fiche c'est le conteneur qui porte
 * l'arrondi et la respiration. La maille vaut donc `largeur / cols` par
 * `hauteur / rows`, et **rien ne garantit qu'elle soit carrée** : sur une tuile
 * de 200 × 150, une grille 28 × 13 donne des mailles de 7,1 × 11,5.
 *
 * On dessine malgré tout un **carré**, de côté `min(maille) - gap`, centré dans
 * sa maille. Le pas de la grille est inchangé — seule la marque devient carrée.
 * Une trame de carrés se lit comme un semis de points ; une trame de rectangles
 * hauts se lit comme des barres, donc comme un axe, donc comme le piano-roll —
 * exactement la fausse promesse qu'on vient de retirer à la figure.
 *
 * Le côté se déduisant des dimensions **lues dans la chaîne**, une grille plus
 * fine servie plus tard (28 × 21 rendrait les mailles carrées sur cette tuile)
 * se dessine correctement sans toucher à ce module ni au composant.
 *
 * # La fenêtre de rangs
 *
 * Avec un `window`, le cadre ne représente plus toute la grille mais
 * `rowCount` rangs à partir de `rowStart` — voir `SilhouetteRowWindow`. Les
 * repères (`silhouetteRowOffset`, `handTerritory`) restent, eux, à l'échelle
 * de la grille entière : une figure rognée n'en porte pas, et une bande de
 * pochette de 37 px de haut n'a pas de place pour une règle tous les quatre
 * rangs.
 */
export function silhouetteRects(
  silhouette: Silhouette,
  box: SilhouetteBox,
  window: SilhouetteRowWindow | null = null,
): readonly SilhouetteRect[] {
  const rowStart = window === null ? 0 : window.rowStart;
  const rowCount = window === null ? silhouette.rows : window.rowCount;
  const cellWidth = box.width / silhouette.cols;
  const cellHeight = box.height / rowCount;
  const side = Math.max(
    MIN_CELL_SIZE,
    Math.min(cellWidth, cellHeight) - box.gap,
  );

  const rects: SilhouetteRect[] = [];

  for (const cell of silhouette.cells) {
    const row = cell.row - rowStart;

    if (row < 0 || row >= rowCount) {
      continue;
    }

    rects.push({
      ...cell,
      x: cell.col * cellWidth + (cellWidth - side) / 2,
      y: row * cellHeight + (cellHeight - side) / 2,
      width: side,
      height: side,
    });
  }

  return rects;
}

/**
 * La fenêtre de `rowCount` rangs contigus qui porte le plus de cases.
 *
 * La plus dense, et non le milieu de la grille : la grille est normalisée sur
 * l'amplitude du morceau, pas sur celle du clavier, donc son centre n'a aucune
 * raison d'être là où le morceau vit. Sur une pièce à main gauche tenue, le
 * milieu tombe souvent dans le vide qui sépare les deux mains — et une
 * pochette qui montrerait ce vide dirait « rien ne se passe » d'un morceau
 * nourri.
 *
 * Une passe sur les cases, une somme glissante sur les rangs. `rowCount` plus
 * grand que la grille rend la grille entière.
 */
export function densestRowWindow(
  silhouette: Silhouette,
  rowCount: number,
): SilhouetteRowWindow {
  const count = Math.max(1, Math.min(Math.round(rowCount), silhouette.rows));
  const perRow = new Array<number>(silhouette.rows).fill(0);

  for (const cell of silhouette.cells) {
    perRow[cell.row] += 1;
  }

  let running = perRow.slice(0, count).reduce((sum, value) => sum + value, 0);
  let best = running;
  let bestStart = 0;

  for (let start = 1; start + count <= silhouette.rows; start++) {
    running += perRow[start + count - 1] - perRow[start - 1];

    // Strictement supérieur : à égalité on garde la fenêtre la plus haute,
    // c'est-à-dire la plus aiguë. C'est là que la mélodie se trouve, et une
    // pochette qui hésiterait entre deux fenêtres identiques doit trancher de
    // façon stable — sinon elle change au moindre recalcul.
    if (running > best) {
      best = running;
      bestStart = start;
    }
  }

  return { rowStart: bestStart, rowCount: count };
}

/**
 * Ordonnée d'une frontière de rang dans le cadre, en unités du repère.
 *
 * `row` est un numéro de **frontière** et non de case : `0` est le bord haut,
 * `rows` le bord bas. C'est ce qui permet de tracer aussi bien une règle sur la
 * ligne d'un rang que la limite basse du dernier rang d'une bande.
 */
export function silhouetteRowOffset(
  silhouette: Silhouette,
  box: SilhouetteBox,
  row: number,
): number {
  return (row * box.height) / silhouette.rows;
}

/** La bande verticale occupée par une main dans le cadre. */
export type SilhouetteTerritory = {
  readonly y: number;
  readonly height: number;
};

/**
 * L'étendue verticale d'une main : du haut de son rang le plus aigu au bas de
 * son rang le plus grave. `null` quand la main n'a aucune case.
 *
 * # Ce que ça sert à dire
 *
 * `MIN_PITCH_SPAN` côté serveur impose une amplitude minimale à la grille :
 * une pièce qui tient sur cinq touches occupe donc quelques rangs sur vingt et
 * un, et le reste du cadre est vide. Ce vide **est** l'information — « ce
 * morceau ne bouge pas » — mais un cadre vide ressemble à un rendu raté. La
 * bande donne au vide une raison d'être là.
 *
 * Sur une pièce nourrie les deux bandes couvrent presque tout le cadre et se
 * neutralisent l'une l'autre : le traitement s'efface là où il n'a rien à
 * dire, ce qui est le comportement voulu et non un effet de bord.
 */
export function handTerritory(
  silhouette: Silhouette,
  box: SilhouetteBox,
  hand: SilhouetteHand,
): SilhouetteTerritory | null {
  let firstRow = Number.POSITIVE_INFINITY;
  let lastRow = Number.NEGATIVE_INFINITY;

  for (const cell of silhouette.cells) {
    if (cell.hand !== hand) {
      continue;
    }

    firstRow = Math.min(firstRow, cell.row);
    lastRow = Math.max(lastRow, cell.row);
  }

  if (lastRow < firstRow) {
    return null;
  }

  const y = silhouetteRowOffset(silhouette, box, firstRow);

  return { y, height: silhouetteRowOffset(silhouette, box, lastRow + 1) - y };
}

/** Entier décimal strictement positif, sans signe ni décimale ni espace. */
function parsePositiveInteger(value: string): number | null {
  if (!/^[0-9]+$/.test(value)) {
    return null;
  }

  const parsed = Number(value);

  return parsed > 0 && Number.isSafeInteger(parsed) ? parsed : null;
}

/**
 * Décode le base64 en octets.
 *
 * On valide l'alphabet et le padding **avant** d'appeler `atob` : selon les
 * moteurs, `atob` ignore silencieusement certains caractères hors alphabet au
 * lieu de lever. Une chaîne corrompue rendrait alors des octets plausibles
 * mais faux, donc une figure fausse — exactement ce qu'on veut éviter.
 */
function decodeBase64(value: string): Uint8Array | null {
  if (value.length === 0 || value.length % 4 !== 0) {
    return null;
  }

  if (!/^[A-Za-z0-9+/]+={0,2}$/.test(value)) {
    return null;
  }

  let binary: string;

  try {
    binary = atob(value);
  } catch {
    return null;
  }

  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  return bytes;
}
